import React from 'react';
import {
  Box, Typography, IconButton, CircularProgress, Stack, Chip
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  VideoLibrary as VideoLibraryIcon,
  CalendarMonth as CalendarIcon,
  Category as CategoryIcon,
  AccessTime as DraftIcon,
} from '@mui/icons-material';

/* ─── Design Tokens (matches Vimoir system) ─── */
const T = {
  amber:      '#C2823C',
  amberLight: '#E8A85A',
  sage:       '#6B8C6E',
  rose:       '#C4706A',
  cream:      '#FAF8F4',
  ivory:      '#FFFEFB',
  paper:      '#F5F1EA',
  ink:        '#1a1510',
  inkMid:     '#4a3f30',
  inkLight:   '#8a7055',
  border:     'rgba(194,130,60,0.18)',
  shadow:     '0 8px 32px rgba(100,70,30,0.10)',
  shadowHover:'0 20px 52px rgba(100,70,30,0.18)',
};

/* ─── Single Draft Card ─── */
const DraftCard = ({ item, image, onEdit, onDelete, index }) => {
  const formattedDate = item.eventDate
    ? new Date(item.eventDate).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' })
    : null;

  return (
    <Box
      sx={{
        position: 'relative',
        borderRadius: '20px',
        overflow: 'hidden',
        border: `1px solid ${T.border}`,
        background: T.ivory,
        boxShadow: T.shadow,
        transition: 'all 0.38s cubic-bezier(0.34,1.56,0.64,1)',
        animation: `fadeInUp 0.45s ease ${index * 0.06}s both`,
        '@keyframes fadeInUp': {
          from: { opacity: 0, transform: 'translateY(18px)' },
          to:   { opacity: 1, transform: 'translateY(0)' },
        },
        '&:hover': {
          transform: 'translateY(-6px) scale(1.012)',
          boxShadow: T.shadowHover,
          border: `1px solid rgba(194,130,60,0.35)`,
          '& .card-img': { transform: 'scale(1.07)' },
          '& .card-actions': { opacity: 1, transform: 'translateY(0)' },
        },
      }}
    >
      {/* Image area */}
      <Box sx={{ height: 200, overflow: 'hidden', position: 'relative', background: T.paper }}>
        {image ? (
          <Box
            className="card-img"
            component="img"
            src={image}
            alt={item.eventName}
            sx={{ width: '100%', height: '100%', objectFit: 'cover', transition: 'transform 0.5s ease', display: 'block' }}
          />
        ) : (
          <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
            <Box sx={{
              width: 48, height: 48, borderRadius: '14px',
              background: `${T.amber}14`, border: `1px solid ${T.amber}25`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <VideoLibraryIcon sx={{ fontSize: 22, color: T.amber }} />
            </Box>
            <Typography sx={{ fontSize: '0.75rem', color: T.inkLight }}>No preview</Typography>
          </Box>
        )}

        {/* Gradient overlay */}
        <Box sx={{
          position: 'absolute', inset: 0,
          background: 'linear-gradient(to top, rgba(26,21,16,0.65) 0%, transparent 55%)',
          pointerEvents: 'none',
        }} />

        {/* Draft badge top-left */}
        <Box sx={{
          position: 'absolute', top: 10, left: 10,
          display: 'inline-flex', alignItems: 'center', gap: '5px',
          px: '8px', py: '4px', borderRadius: '50px',
          background: 'rgba(250,248,244,0.88)',
          backdropFilter: 'blur(8px)',
          border: `1px solid ${T.border}`,
        }}>
          <DraftIcon sx={{ fontSize: 11, color: T.amber }} />
          <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: T.amber, letterSpacing: '0.1em', textTransform: 'uppercase' }}>
            Draft
          </Typography>
        </Box>

        {/* Action buttons — appear on hover */}
        <Stack
          className="card-actions"
          direction="row"
          spacing={0.7}
          sx={{
            position: 'absolute', top: 10, right: 10,
            opacity: 0, transform: 'translateY(-6px)',
            transition: 'all 0.25s ease',
          }}
        >
          <IconButton
            size="small"
            onClick={(e) => { e.stopPropagation(); onEdit(item); }}
            sx={{
              width: 32, height: 32,
              background: 'rgba(255,255,255,0.88)',
              backdropFilter: 'blur(8px)',
              border: '1px solid rgba(255,255,255,0.4)',
              color: T.inkMid,
              '&:hover': { background: '#fff', color: T.amber, transform: 'scale(1.1)' },
              transition: 'all 0.2s ease',
            }}
          >
            <EditIcon sx={{ fontSize: 15 }} />
          </IconButton>
          <IconButton
            size="small"
            onClick={(e) => { e.stopPropagation(); onDelete(item.draftId); }}
            sx={{
              width: 32, height: 32,
              background: `${T.rose}cc`,
              backdropFilter: 'blur(8px)',
              border: '1px solid rgba(255,255,255,0.25)',
              color: '#fff',
              '&:hover': { background: T.rose, transform: 'scale(1.1)' },
              transition: 'all 0.2s ease',
            }}
          >
            <DeleteIcon sx={{ fontSize: 15 }} />
          </IconButton>
        </Stack>

        {/* Event name pinned to bottom of image */}
        <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, px: 2, pb: 1.5 }}>
          <Typography sx={{
            fontFamily: "'DM Serif Display', serif",
            fontWeight: 400, fontSize: '1.05rem',
            color: '#fff', lineHeight: 1.2,
            textShadow: '0 1px 4px rgba(0,0,0,0.4)',
          }}>
            {item.eventName || 'Untitled draft'}
          </Typography>
        </Box>
      </Box>

      {/* Info footer */}
      <Box sx={{ px: 2, py: 1.5 }}>
        <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ gap: 0.8 }}>
          {item.category && (
            <Chip
              icon={<CategoryIcon sx={{ fontSize: '0.7rem !important', color: `${T.amber} !important` }} />}
              label={item.category}
              size="small"
              sx={{ fontSize: '0.65rem', fontWeight: 600, height: 20, bgcolor: `${T.amber}12`, color: T.amber, border: `1px solid ${T.amber}25` }}
            />
          )}
          {formattedDate && (
            <Chip
              icon={<CalendarIcon sx={{ fontSize: '0.7rem !important', color: `${T.inkLight} !important` }} />}
              label={formattedDate}
              size="small"
              sx={{ fontSize: '0.65rem', fontWeight: 500, height: 20, bgcolor: T.cream, color: T.inkLight, border: `1px solid ${T.border}` }}
            />
          )}
        </Stack>
      </Box>
    </Box>
  );
};

/* ─── DraftsList ─── */
const DraftsList = ({ drafts, draftImages, loading, onEditDraft, onDeleteDraft }) => {
  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 10, gap: 2 }}>
        <CircularProgress sx={{ color: T.amber }} size={32} />
        <Typography sx={{ color: T.inkLight, fontSize: '0.85rem' }}>Loading drafts…</Typography>
      </Box>
    );
  }

  if (!drafts || drafts.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 10, animation: 'fadeInUp 0.5s ease' }}>
        <Box sx={{
          width: 88, height: 88, borderRadius: '50%', mx: 'auto', mb: 3,
          background: `radial-gradient(circle, ${T.amber}12, ${T.amber}04)`,
          border: `1px dashed ${T.amber}35`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <VideoLibraryIcon sx={{ fontSize: 30, color: T.amber }} />
        </Box>
        <Typography sx={{
          fontFamily: "'DM Serif Display', serif",
          fontStyle: 'italic', fontSize: '1.2rem',
          color: T.inkMid, mb: 1,
        }}>
          No drafts yet
        </Typography>
        <Typography sx={{ color: T.inkLight, fontSize: '0.85rem', fontWeight: 300 }}>
          Save a memory as a draft to continue it later
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      {/* Header */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Box sx={{
            width: 3, height: 18, borderRadius: '2px',
            background: `linear-gradient(180deg, ${T.amber}, ${T.amberLight})`,
          }} />
          <Typography sx={{
            fontFamily: "'DM Serif Display', serif",
            fontWeight: 400, fontSize: '1.1rem', color: T.ink,
          }}>
            Saved Drafts
          </Typography>
        </Stack>
        <Box sx={{
          px: 1.5, py: 0.4, borderRadius: '50px',
          background: `${T.amber}14`, border: `1px solid ${T.amber}30`,
        }}>
          <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: T.amber }}>
            {drafts.length} {drafts.length === 1 ? 'draft' : 'drafts'}
          </Typography>
        </Box>
      </Stack>

      {/* Grid */}
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
        gap: 2,
      }}>
        {drafts.map((item, index) => (
          <DraftCard
            key={item._id || index}
            item={item}
            image={draftImages[item._id]}
            onEdit={onEditDraft}
            onDelete={onDeleteDraft}
            index={index}
          />
        ))}
      </Box>
    </Box>
  );
};

export default DraftsList;